/**
 * WorkflowDesigner.jsx
 *
 * A wizard-style UI for building workflow automations attached to Action Item templates.
 * Users can define trigger conditions and add ordered automation steps with action-specific
 * configuration panels. Non-technical users are guided with descriptions and sensible defaults.
 *
 * Architecture:
 *  ┌──────────────────────────────────────────┐
 *  │ Header — workflow name, toggle, save      │
 *  ├──────────────────────────────────────────┤
 *  │ Trigger Configuration Panel               │
 *  │  • DateField / OnCreate / OnStatusChange  │
 *  ├──────────────────────────────────────────┤
 *  │ Automation Steps (ordered cards)          │
 *  │  • Drag-to-reorder, add, remove          │
 *  │  • Each step has an action-specific form  │
 *  ├──────────────────────────────────────────┤
 *  │ Footer — Cancel / Save                    │
 *  └──────────────────────────────────────────┘
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';
import { toast } from 'react-toastify';
import {
    FaBolt, FaPlus, FaTrash, FaSave, FaTimes, FaSpinner,
    FaChevronUp, FaChevronDown, FaToggleOn, FaToggleOff,
    FaClock, FaPlay, FaExchangeAlt, FaHandPointer,
    FaUserLock, FaUserSlash, FaEnvelopeOpenText, FaBell,
    FaEdit, FaProjectDiagram, FaGlobe, FaGripVertical,
    FaInfoCircle, FaExclamationTriangle, FaUserPlus, FaComment,
    FaRedo, FaCalendarTimes, FaFilter
} from 'react-icons/fa';
import * as Sentry from '@sentry/react';
import WorkflowSearchableSelect from './WorkflowSearchableSelect';
import '@/styles/WorkflowDesigner.scss';

// ── Icon map for action types ──
const ACTION_ICONS = {
    DisableEntraUser: FaUserLock,
    DisableCapTracUser: FaUserSlash,
    DeleteEmailsByRole: FaEnvelopeOpenText,
    SendNotification: FaBell,
    SetField: FaEdit,
    CreateChildActionItem: FaProjectDiagram,
    CallWebhook: FaGlobe
};

// ── Trigger type metadata ──
const TRIGGER_TYPES = [
    {
        value: 'DateField',
        label: 'Date Field Trigger',
        icon: FaClock,
        description: 'Fires automatically at a specific time relative to a date field in the action item. Perfect for offboarding — trigger at COB on the last working day.',
        color: '#3182ce'
    },
    {
        value: 'OnCreate',
        label: 'On Creation',
        icon: FaPlay,
        description: 'Fires immediately when a new action item is created using this template. Good for auto-populating fields or sending initial notifications.',
        color: '#38a169'
    },
    {
        value: 'OnStatusChange',
        label: 'On Status Change',
        icon: FaExchangeAlt,
        description: 'Fires when the action item\'s status changes to a specific value. Useful for closing-out automations when a ticket is resolved.',
        color: '#d69e2e'
    },
    {
        value: 'OnFieldChange',
        label: 'On Field Change',
        icon: FaEdit,
        description: 'Fires when a specific field value changes on the action item. Use for cascading updates or conditional logic.',
        color: '#e53e3e'
    },
    {
        value: 'OnAssignment',
        label: 'On Assignment',
        icon: FaUserPlus,
        description: 'Fires when the action item is assigned or reassigned to a user. Useful for onboarding notifications or task setup.',
        color: '#319795'
    },
    {
        value: 'OnComment',
        label: 'On Comment Added',
        icon: FaComment,
        description: 'Fires when a comment is added to the action item. Good for escalation or engagement tracking.',
        color: '#6b46c1'
    },
    {
        value: 'OnOverdue',
        label: 'On Overdue',
        icon: FaCalendarTimes,
        description: 'Fires when the action item passes its due date without being completed. Perfect for escalation workflows.',
        color: '#c53030'
    },
    {
        value: 'Recurring',
        label: 'Recurring Schedule',
        icon: FaRedo,
        description: 'Fires on a regular schedule (interval or cron) for items matching conditions. Use for periodic checks or reminders.',
        color: '#2b6cb0'
    },
    {
        value: 'Manual',
        label: 'Manual Trigger',
        icon: FaHandPointer,
        description: 'Only fires when a user explicitly clicks "Run Workflow" from the action item. Gives full human control.',
        color: '#805ad5'
    }
];

const STATUS_OPTIONS = ['Open', 'On-hold', 'Pending', 'In Progress', 'Solved', 'Cancelled', 'Closed'];

const WorkflowDesigner = ({ templateKey, templateSchema, onClose, existingWorkflow = null }) => {
    const { apiFetch, user } = useAuth();

    // ── Form state ──
    const [name, setName] = useState(existingWorkflow?.name || '');
    const [description, setDescription] = useState(existingWorkflow?.description || '');
    const [triggerType, setTriggerType] = useState(existingWorkflow?.triggerType || 'DateField');
    const [triggerConfig, setTriggerConfig] = useState(existingWorkflow?.triggerConfig || {});
    const [isActive, setIsActive] = useState(existingWorkflow?.isActive ?? true);
    const [steps, setSteps] = useState(existingWorkflow?.steps || []);

    // ── Catalog + UI state ──
    const [actionTypes, setActionTypes] = useState([]);
    const [loadingTypes, setLoadingTypes] = useState(true);
    const [saving, setSaving] = useState(false);
    const [expandedStep, setExpandedStep] = useState(null);
    const [showAddStep, setShowAddStep] = useState(false);

    // ── Conditions state ──
    const [conditions, setConditions] = useState(existingWorkflow?.conditions
        ? (typeof existingWorkflow.conditions === 'string' ? JSON.parse(existingWorkflow.conditions) : existingWorkflow.conditions)
        : { logic: 'AND', conditions: [] }
    );
    const [maxExecutionsPerItem, setMaxExecutionsPerItem] = useState(existingWorkflow?.maxExecutionsPerItem || 0);

    // Condition operators
    const CONDITION_OPERATORS = [
        { value: 'equals', label: 'equals' },
        { value: 'notEquals', label: 'does not equal' },
        { value: 'contains', label: 'contains' },
        { value: 'greaterThan', label: 'is greater than' },
        { value: 'lessThan', label: 'is less than' },
        { value: 'isEmpty', label: 'is empty' },
        { value: 'isNotEmpty', label: 'is not empty' },
        { value: 'in', label: 'is one of' },
        { value: 'notIn', label: 'is not one of' },
        { value: 'matchesRegex', label: 'matches pattern' }
    ];

    // ── Lookup loaders (searchable pickers) ──
    const loadUsers = useCallback(async (search) => {
        const res = await apiFetch(`/api/WorkflowAutomations/lookup/users?search=${encodeURIComponent(search || '')}`);
        const data = await res.json();
        return (data.data || []).map(u => ({
            id: u.id,
            label: u.label,
            sub: u.source === 'Microsoft 365' ? `${u.email} · Microsoft 365` : u.email
        }));
    }, [apiFetch]);

    const loadRoles = useCallback(async (search) => {
        const res = await apiFetch(`/api/WorkflowAutomations/lookup/roles?search=${encodeURIComponent(search || '')}`);
        const data = await res.json();
        return (data.data || []).map(r => ({ id: r.id, label: r.label }));
    }, [apiFetch]);

    const loadDepartments = useCallback(async (search) => {
        const res = await apiFetch(`/api/WorkflowAutomations/lookup/departments?search=${encodeURIComponent(search || '')}`);
        const data = await res.json();
        return (data.data || []).map(d => ({ id: d.id, label: d.label }));
    }, [apiFetch]);

    const loadTeams = useCallback(async (search) => {
        const res = await apiFetch(`/api/NotificationChannels/discover/teams?secretId=0`);
        if (!res.ok) return [];
        const data = await res.json();
        const teams = (data.data || []).map(t => ({ id: t.id, label: t.displayName }));
        if (search) {
            const term = search.toLowerCase();
            return teams.filter(t => t.label.toLowerCase().includes(term));
        }
        return teams;
    }, [apiFetch]);

    const makeLoadChannels = useCallback((teamId) => async (search) => {
        if (!teamId) return [];
        const res = await apiFetch(`/api/NotificationChannels/discover/teams/${teamId}/channels?secretId=0`);
        if (!res.ok) return [];
        const data = await res.json();
        const channels = (data.data || []).map(c => ({ id: c.id, label: c.displayName, sub: c.membershipType }));
        if (search) {
            const term = search.toLowerCase();
            return channels.filter(c => c.label.toLowerCase().includes(term));
        }
        return channels;
    }, [apiFetch]);

    // ── Template fields for field-ref dropdowns ──
    const templateFields = React.useMemo(() => {
        if (!templateSchema) return [];
        const schema = typeof templateSchema === 'string' ? JSON.parse(templateSchema) : templateSchema;
        const fields = [];
        const extractFields = (items) => {
            (items || []).forEach(f => {
                if (f.type === 'row' && f.columns) {
                    f.columns.forEach(col => extractFields(col.fields));
                } else if (f.name) {
                    fields.push({ name: f.name, label: f.label || f.name, type: f.type });
                }
            });
        };
        if (schema.sections) {
            schema.sections.forEach(s => extractFields(s.fields));
        } else {
            extractFields(schema.fields);
        }
        return fields;
    }, [templateSchema]);

    // Date fields only (for trigger config)
    const dateFields = templateFields.filter(f => f.type === 'date' || f.type === 'datetime');

    // ── Load action type catalog ──
    useEffect(() => {
        const loadTypes = async () => {
            try {
                const res = await apiFetch('/api/WorkflowAutomations/action-types');
                const data = await res.json();
                if (data.success) setActionTypes(data.data || []);
            } catch (err) {
                Sentry.captureException(err);
            } finally {
                setLoadingTypes(false);
            }
        };
        loadTypes();
    }, [apiFetch]);

    // ── Step management ──
    const addStep = (actionType) => {
        const typeMeta = actionTypes.find(t => t.type === actionType);
        setSteps(prev => [
            ...prev,
            {
                _id: `step_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
                actionType,
                actionConfig: {},
                name: typeMeta?.label || actionType,
                description: typeMeta?.description || '',
                isActive: true,
                continueOnFailure: false
            }
        ]);
        setShowAddStep(false);
        setExpandedStep(steps.length); // expand the newly added step
    };

    const removeStep = (index) => {
        setSteps(prev => prev.filter((_, i) => i !== index));
        if (expandedStep === index) setExpandedStep(null);
    };

    const moveStep = (index, direction) => {
        setSteps(prev => {
            const next = [...prev];
            const targetIndex = index + direction;
            if (targetIndex < 0 || targetIndex >= next.length) return prev;
            [next[index], next[targetIndex]] = [next[targetIndex], next[index]];
            return next;
        });
        setExpandedStep(prev => prev === index ? index + direction : prev);
    };

    const updateStep = (index, updates) => {
        setSteps(prev => prev.map((s, i) => i === index ? { ...s, ...updates } : s));
    };

    const updateStepConfig = (index, key, value) => {
        setSteps(prev => prev.map((s, i) =>
            i === index ? { ...s, actionConfig: { ...s.actionConfig, [key]: value } } : s
        ));
    };

    // ── Save ──
    const handleSave = async () => {
        if (!name.trim()) {
            toast.error('Workflow name is required');
            return;
        }
        if (steps.length === 0) {
            toast.error('Add at least one automation step');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                templateKey,
                name: name.trim(),
                description: description.trim() || null,
                triggerType,
                triggerConfig,
                isActive,
                sortOrder: 0,
                conditions: (conditions.conditions || []).length > 0 ? JSON.stringify(conditions) : null,
                maxExecutionsPerItem,
                createdByUserId: user?.userID || user?.id || 0,
                modifiedByUserId: user?.userID || user?.id || 0,
                steps: steps.map(s => ({
                    actionType: s.actionType,
                    actionConfig: s.actionConfig || {},
                    name: s.name,
                    description: s.description || null,
                    isActive: s.isActive,
                    continueOnFailure: s.continueOnFailure
                }))
            };

            const url = existingWorkflow?.id
                ? `/api/WorkflowAutomations/${existingWorkflow.id}`
                : '/api/WorkflowAutomations';
            const method = existingWorkflow?.id ? 'PUT' : 'POST';

            const res = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });
            const data = await res.json();

            if (res.ok && data.success) {
                toast.success(existingWorkflow ? 'Workflow updated' : 'Workflow created');
                if (onClose) onClose(true);
            } else {
                toast.error(data.message || 'Failed to save workflow');
            }
        } catch (err) {
            Sentry.captureException(err);
            toast.error('Error saving workflow');
        } finally {
            setSaving(false);
        }
    };

    // ── Conditions builder panel ──
    const renderConditionsBuilder = () => {
        const condList = conditions.conditions || [];

        const addCondition = () => {
            setConditions(prev => ({
                ...prev,
                conditions: [...(prev.conditions || []), { field: '', operator: 'equals', value: '' }]
            }));
        };

        const updateCondition = (idx, updates) => {
            setConditions(prev => ({
                ...prev,
                conditions: prev.conditions.map((c, i) => i === idx ? { ...c, ...updates } : c)
            }));
        };

        const removeCondition = (idx) => {
            setConditions(prev => ({
                ...prev,
                conditions: prev.conditions.filter((_, i) => i !== idx)
            }));
        };

        // Build field options from template fields + standard fields
        const allFieldOpts = [
            { value: 'Status', label: 'Status' },
            { value: 'Priority', label: 'Priority' },
            { value: 'AssignedToUserID', label: 'Assigned User' },
            ...templateFields.map(f => ({ value: f.name, label: f.label }))
        ];

        return (
            <div className="wfd-conditions-builder">
                {condList.length > 0 && (
                    <div className="wfd-conditions-logic">
                        <button
                            type="button"
                            className={`wfd-logic-btn ${conditions.logic === 'AND' ? 'active' : ''}`}
                            onClick={() => setConditions(prev => ({ ...prev, logic: 'AND' }))}
                        >
                            ALL must match
                        </button>
                        <button
                            type="button"
                            className={`wfd-logic-btn ${conditions.logic === 'OR' ? 'active' : ''}`}
                            onClick={() => setConditions(prev => ({ ...prev, logic: 'OR' }))}
                        >
                            ANY must match
                        </button>
                    </div>
                )}
                {condList.map((cond, idx) => (
                    <div key={idx} className="wfd-condition-row">
                        <select
                            value={cond.field}
                            onChange={e => updateCondition(idx, { field: e.target.value })}
                            className="wfd-select wfd-cond-field"
                        >
                            <option value="">-- Field --</option>
                            {allFieldOpts.map(f => (
                                <option key={f.value} value={f.value}>{f.label}</option>
                            ))}
                        </select>
                        <select
                            value={cond.operator}
                            onChange={e => updateCondition(idx, { operator: e.target.value })}
                            className="wfd-select wfd-cond-op"
                        >
                            {CONDITION_OPERATORS.map(op => (
                                <option key={op.value} value={op.value}>{op.label}</option>
                            ))}
                        </select>
                        {!['isEmpty', 'isNotEmpty'].includes(cond.operator) && (
                            <input
                                type="text"
                                value={cond.value || ''}
                                onChange={e => updateCondition(idx, { value: e.target.value })}
                                className="wfd-input wfd-cond-val"
                                placeholder="Value..."
                            />
                        )}
                        <button
                            type="button"
                            className="wfd-step-btn wfd-cond-remove"
                            onClick={() => removeCondition(idx)}
                            title="Remove condition"
                        >
                            <FaTimes />
                        </button>
                    </div>
                ))}
                <button type="button" className="wfd-add-condition-btn" onClick={addCondition}>
                    <FaPlus /> Add Condition
                </button>

                {/* Max executions per item */}
                <div className="wfd-config-field" style={{ marginTop: '12px' }}>
                    <label>Max executions per item</label>
                    <input
                        type="number"
                        value={maxExecutionsPerItem}
                        onChange={e => setMaxExecutionsPerItem(parseInt(e.target.value) || 0)}
                        className="wfd-input"
                        min={0}
                        style={{ width: '100px' }}
                    />
                    <small>0 = unlimited. Prevents infinite loops for recurring triggers.</small>
                </div>
            </div>
        );
    };

    // ── Trigger config panel ──
    const renderTriggerConfig = () => {
        switch (triggerType) {
            case 'DateField':
                return (
                    <div className="wfd-trigger-config">
                        <div className="wfd-config-row">
                            <div className="wfd-config-field">
                                <label>Date Field</label>
                                <select
                                    value={triggerConfig.dateField || ''}
                                    onChange={e => setTriggerConfig(prev => ({ ...prev, dateField: e.target.value }))}
                                    className="wfd-select"
                                >
                                    <option value="">-- Select a date field --</option>
                                    {dateFields.map(f => (
                                        <option key={f.name} value={f.name}>{f.label}</option>
                                    ))}
                                </select>
                                <small>The date field in the template that determines when the workflow fires</small>
                            </div>
                            <div className="wfd-config-field">
                                <label>Time of Day</label>
                                <input
                                    type="time"
                                    value={triggerConfig.timeOfDay || '17:00'}
                                    onChange={e => setTriggerConfig(prev => ({ ...prev, timeOfDay: e.target.value }))}
                                    className="wfd-input"
                                />
                                <small>The time on the trigger date when the workflow should execute (default: 17:00 COB)</small>
                            </div>
                        </div>
                        <div className="wfd-config-row">
                            <div className="wfd-config-field">
                                <label>Offset Days</label>
                                <input
                                    type="number"
                                    value={triggerConfig.offsetDays || 0}
                                    onChange={e => setTriggerConfig(prev => ({ ...prev, offsetDays: parseInt(e.target.value) || 0 }))}
                                    className="wfd-input"
                                    min={-30}
                                    max={30}
                                />
                                <small>Days before (-) or after (+) the date field value. 0 = same day.</small>
                            </div>
                            <div className="wfd-config-field">
                                <label>Timezone</label>
                                <select
                                    value={triggerConfig.timezone || 'Africa/Johannesburg'}
                                    onChange={e => setTriggerConfig(prev => ({ ...prev, timezone: e.target.value }))}
                                    className="wfd-select"
                                >
                                    <option value="Africa/Johannesburg">South Africa (SAST)</option>
                                    <option value="UTC">UTC</option>
                                    <option value="Europe/London">London (GMT/BST)</option>
                                    <option value="America/New_York">New York (EST/EDT)</option>
                                </select>
                                <small>The timezone for evaluating the trigger time</small>
                            </div>
                        </div>
                    </div>
                );

            case 'OnStatusChange':
                return (
                    <div className="wfd-trigger-config">
                        <div className="wfd-config-field">
                            <label>Target Status</label>
                            <select
                                value={triggerConfig.targetStatus || ''}
                                onChange={e => setTriggerConfig(prev => ({ ...prev, targetStatus: e.target.value }))}
                                className="wfd-select"
                            >
                                <option value="">-- Select status --</option>
                                {STATUS_OPTIONS.map(s => (
                                    <option key={s} value={s}>{s}</option>
                                ))}
                            </select>
                            <small>The workflow fires when the action item's status changes to this value</small>
                        </div>
                    </div>
                );

            case 'OnCreate':
                return (
                    <div className="wfd-trigger-config wfd-trigger-info">
                        <FaInfoCircle />
                        <span>This workflow will fire immediately when a new action item is created using this template.</span>
                    </div>
                );

            case 'OnFieldChange':
                return (
                    <div className="wfd-trigger-config">
                        <div className="wfd-config-row">
                            <div className="wfd-config-field">
                                <label>Watch Field</label>
                                <select
                                    value={triggerConfig.watchField || ''}
                                    onChange={e => setTriggerConfig(prev => ({ ...prev, watchField: e.target.value }))}
                                    className="wfd-select"
                                >
                                    <option value="">-- Select a field --</option>
                                    {templateFields.map(f => (
                                        <option key={f.name} value={f.name}>{f.label} ({f.type})</option>
                                    ))}
                                    <option value="Status">Status</option>
                                    <option value="Priority">Priority</option>
                                    <option value="AssignedToUserID">Assigned User</option>
                                </select>
                                <small>The field to watch for changes</small>
                            </div>
                            <div className="wfd-config-field">
                                <label>Target Value (optional)</label>
                                <input
                                    type="text"
                                    value={triggerConfig.targetValue || ''}
                                    onChange={e => setTriggerConfig(prev => ({ ...prev, targetValue: e.target.value }))}
                                    className="wfd-input"
                                    placeholder="Leave empty to fire on any change"
                                />
                                <small>If set, only fires when the field changes to this specific value</small>
                            </div>
                        </div>
                    </div>
                );

            case 'OnAssignment':
                return (
                    <div className="wfd-trigger-config wfd-trigger-info">
                        <FaInfoCircle />
                        <span>This workflow will fire whenever the action item is assigned or reassigned to a user.</span>
                    </div>
                );

            case 'OnComment':
                return (
                    <div className="wfd-trigger-config">
                        <div className="wfd-config-field">
                            <label>Comment Contains (optional)</label>
                            <input
                                type="text"
                                value={triggerConfig.containsText || ''}
                                onChange={e => setTriggerConfig(prev => ({ ...prev, containsText: e.target.value }))}
                                className="wfd-input"
                                placeholder="Leave empty to fire on any comment"
                            />
                            <small>If set, only fires when the comment contains this text (case-insensitive)</small>
                        </div>
                    </div>
                );

            case 'OnOverdue':
                return (
                    <div className="wfd-trigger-config">
                        <div className="wfd-config-row">
                            <div className="wfd-config-field">
                                <label>Grace Period (days)</label>
                                <input
                                    type="number"
                                    value={triggerConfig.graceDays || 0}
                                    onChange={e => setTriggerConfig(prev => ({ ...prev, graceDays: parseInt(e.target.value) || 0 }))}
                                    className="wfd-input"
                                    min={0}
                                    max={30}
                                />
                                <small>Days after the due date before this workflow fires. 0 = immediately on overdue.</small>
                            </div>
                            <div className="wfd-config-field">
                                <label>Due Date Field</label>
                                <select
                                    value={triggerConfig.dueDateField || ''}
                                    onChange={e => setTriggerConfig(prev => ({ ...prev, dueDateField: e.target.value }))}
                                    className="wfd-select"
                                >
                                    <option value="">-- Use standard Due Date --</option>
                                    {dateFields.map(f => (
                                        <option key={f.name} value={f.name}>{f.label}</option>
                                    ))}
                                </select>
                                <small>Which date field determines "overdue" (default: action item Due Date)</small>
                            </div>
                        </div>
                    </div>
                );

            case 'Recurring':
                return (
                    <div className="wfd-trigger-config">
                        <div className="wfd-config-row">
                            <div className="wfd-config-field">
                                <label>Interval</label>
                                <input
                                    type="number"
                                    value={triggerConfig.intervalValue || 1}
                                    onChange={e => setTriggerConfig(prev => ({ ...prev, intervalValue: parseInt(e.target.value) || 1 }))}
                                    className="wfd-input"
                                    min={1}
                                />
                            </div>
                            <div className="wfd-config-field">
                                <label>Unit</label>
                                <select
                                    value={triggerConfig.intervalUnit || 'hours'}
                                    onChange={e => setTriggerConfig(prev => ({ ...prev, intervalUnit: e.target.value }))}
                                    className="wfd-select"
                                >
                                    <option value="minutes">Minutes</option>
                                    <option value="hours">Hours</option>
                                    <option value="days">Days</option>
                                </select>
                            </div>
                            <div className="wfd-config-field">
                                <label>Timezone</label>
                                <select
                                    value={triggerConfig.timezone || 'Africa/Johannesburg'}
                                    onChange={e => setTriggerConfig(prev => ({ ...prev, timezone: e.target.value }))}
                                    className="wfd-select"
                                >
                                    <option value="Africa/Johannesburg">South Africa (SAST)</option>
                                    <option value="UTC">UTC</option>
                                    <option value="Europe/London">London (GMT/BST)</option>
                                    <option value="America/New_York">New York (EST/EDT)</option>
                                </select>
                            </div>
                        </div>
                        <small style={{ display: 'block', marginTop: '8px', color: '#718096' }}>
                            The workflow runs at this interval for all action items matching conditions.
                        </small>
                    </div>
                );

            case 'Manual':
                return (
                    <div className="wfd-trigger-config wfd-trigger-info">
                        <FaInfoCircle />
                        <span>This workflow will only run when a user clicks "Run Workflow" from the action item detail page.</span>
                    </div>
                );

            default:
                return null;
        }
    };

    // ── Step config panel (renders action-specific fields) ──
    const renderStepConfig = (step, index) => {
        const typeMeta = actionTypes.find(t => t.type === step.actionType);
        if (!typeMeta?.configFields) return null;

        return (
            <div className="wfd-step-config">
                {typeMeta.configFields.map(cf => (
                    <div key={cf.name} className="wfd-config-field">
                        <label>
                            {cf.label}
                            {cf.required && <span className="wfd-required">*</span>}
                        </label>
                        {renderConfigInput(cf, step, index)}
                        {cf.helpText && <small>{cf.helpText}</small>}
                    </div>
                ))}
            </div>
        );
    };

    const renderConfigInput = (configField, step, stepIndex) => {
        const value = step.actionConfig?.[configField.name] ?? '';

        switch (configField.type) {
            case 'fieldRef':
                return (
                    <select
                        value={value}
                        onChange={e => updateStepConfig(stepIndex, configField.name, e.target.value)}
                        className="wfd-select"
                    >
                        <option value="">-- Select field --</option>
                        {templateFields.map(f => (
                            <option key={f.name} value={f.name}>{f.label} ({f.type})</option>
                        ))}
                    </select>
                );

            case 'select':
                return (
                    <select
                        value={value}
                        onChange={e => updateStepConfig(stepIndex, configField.name, e.target.value)}
                        className="wfd-select"
                    >
                        <option value="">-- Select --</option>
                        {(configField.options || []).map(opt => (
                            <option key={opt} value={opt}>{opt}</option>
                        ))}
                    </select>
                );

            case 'boolean':
                return (
                    <label className="wfd-toggle-label">
                        <input
                            type="checkbox"
                            checked={!!value}
                            onChange={e => updateStepConfig(stepIndex, configField.name, e.target.checked)}
                        />
                        <span>{value ? 'Yes' : 'No'}</span>
                    </label>
                );

            case 'textarea':
                return (
                    <textarea
                        value={value}
                        onChange={e => updateStepConfig(stepIndex, configField.name, e.target.value)}
                        className="wfd-textarea"
                        rows={4}
                        placeholder={configField.placeholder || ''}
                    />
                );

            case 'url':
                return (
                    <input
                        type="url"
                        value={value}
                        onChange={e => updateStepConfig(stepIndex, configField.name, e.target.value)}
                        className="wfd-input"
                        placeholder={configField.placeholder || 'https://...'}
                    />
                );

            case 'templateRef':
                return (
                    <input
                        type="text"
                        value={value}
                        onChange={e => updateStepConfig(stepIndex, configField.name, e.target.value)}
                        className="wfd-input"
                        placeholder="Enter template key..."
                    />
                );

            // ── Searchable pickers ──────────────────────────────────────────
            case 'recipientPicker': {
                const recipientType = step.actionConfig?.recipientType || 'User';
                let loader = loadUsers;
                if (recipientType === 'Role') loader = loadRoles;
                else if (recipientType === 'Department') loader = loadDepartments;

                if (recipientType === 'FieldValue') {
                    return (
                        <select
                            value={value}
                            onChange={e => updateStepConfig(stepIndex, configField.name, e.target.value)}
                            className="wfd-select"
                        >
                            <option value="">-- Select field --</option>
                            {templateFields.map(f => (
                                <option key={f.name} value={f.name}>{f.label}</option>
                            ))}
                        </select>
                    );
                }

                const displayLabel = step.actionConfig?.[`_${configField.name}_label`] || value;
                return (
                    <WorkflowSearchableSelect
                        value={value}
                        displayValue={displayLabel}
                        loadOptions={loader}
                        placeholder={`Search ${recipientType.toLowerCase()}s...`}
                        onSelect={(val, label) => {
                            updateStep(stepIndex, {
                                actionConfig: {
                                    ...step.actionConfig,
                                    [configField.name]: val,
                                    [`_${configField.name}_label`]: label
                                }
                            });
                        }}
                    />
                );
            }

            case 'teamsPicker': {
                const displayLabel = step.actionConfig?._teamsTeamId_label || value;
                return (
                    <WorkflowSearchableSelect
                        value={value}
                        displayValue={displayLabel}
                        loadOptions={loadTeams}
                        placeholder="Search Teams..."
                        onSelect={(val, label) => {
                            updateStep(stepIndex, {
                                actionConfig: {
                                    ...step.actionConfig,
                                    teamsTeamId: val,
                                    _teamsTeamId_label: label,
                                    // reset channel when team changes
                                    teamsChannelId: '',
                                    _teamsChannelId_label: ''
                                }
                            });
                        }}
                    />
                );
            }

            case 'teamsChannelPicker': {
                const teamId = step.actionConfig?.teamsTeamId || '';
                const displayLabel = step.actionConfig?._teamsChannelId_label || value;
                return (
                    <WorkflowSearchableSelect
                        value={value}
                        displayValue={displayLabel}
                        loadOptions={makeLoadChannels(teamId)}
                        placeholder={teamId ? 'Search channels...' : 'Select a team first'}
                        disabled={!teamId}
                        onSelect={(val, label) => {
                            updateStep(stepIndex, {
                                actionConfig: {
                                    ...step.actionConfig,
                                    teamsChannelId: val,
                                    _teamsChannelId_label: label
                                }
                            });
                        }}
                    />
                );
            }

            case 'setFieldValue': {
                const fieldName = step.actionConfig?.fieldName || '';
                if (fieldName === 'AssignedToUserID') {
                    const displayLabel = step.actionConfig?._value_label || value;
                    return (
                        <WorkflowSearchableSelect
                            value={value}
                            displayValue={displayLabel}
                            loadOptions={loadUsers}
                            placeholder="Search users..."
                            onSelect={(val, label) => {
                                updateStep(stepIndex, {
                                    actionConfig: {
                                        ...step.actionConfig,
                                        value: val,
                                        _value_label: label
                                    }
                                });
                            }}
                        />
                    );
                }
                if (fieldName === 'AssignedToDepartmentID') {
                    const displayLabel = step.actionConfig?._value_label || value;
                    return (
                        <WorkflowSearchableSelect
                            value={value}
                            displayValue={displayLabel}
                            loadOptions={loadDepartments}
                            placeholder="Search departments..."
                            onSelect={(val, label) => {
                                updateStep(stepIndex, {
                                    actionConfig: {
                                        ...step.actionConfig,
                                        value: val,
                                        _value_label: label
                                    }
                                });
                            }}
                        />
                    );
                }
                if (fieldName === 'Status') {
                    return (
                        <select
                            value={value}
                            onChange={e => updateStepConfig(stepIndex, configField.name, e.target.value)}
                            className="wfd-select"
                        >
                            <option value="">-- Select status --</option>
                            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
                        </select>
                    );
                }
                if (fieldName === 'Priority') {
                    return (
                        <select
                            value={value}
                            onChange={e => updateStepConfig(stepIndex, configField.name, e.target.value)}
                            className="wfd-select"
                        >
                            <option value="">-- Select priority --</option>
                            {['Low', 'Medium', 'High', 'Critical'].map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                    );
                }
                // Generic fallback
                return (
                    <input
                        type="text"
                        value={value}
                        onChange={e => updateStepConfig(stepIndex, configField.name, e.target.value)}
                        className="wfd-input"
                        placeholder={configField.placeholder || ''}
                    />
                );
            }

            default:
                return (
                    <input
                        type="text"
                        value={value}
                        onChange={e => updateStepConfig(stepIndex, configField.name, e.target.value)}
                        className="wfd-input"
                        placeholder={configField.placeholder || ''}
                    />
                );
        }
    };

    // ── Group action types by category ──
    const groupedActions = React.useMemo(() => {
        const groups = {};
        actionTypes.forEach(at => {
            const cat = at.category || 'Other';
            if (!groups[cat]) groups[cat] = [];
            groups[cat].push(at);
        });
        return groups;
    }, [actionTypes]);

    // ── Render ──
    return (
        <div className="wfd-backdrop" onClick={() => onClose && onClose(false)}>
            <div className="wfd-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="wfd-header">
                    <div className="wfd-header-left">
                        <FaBolt className="wfd-header-icon" />
                        <h2>{existingWorkflow ? 'Edit Workflow' : 'New Workflow Automation'}</h2>
                    </div>
                    <div className="wfd-header-right">
                        <button
                            className={`wfd-toggle-btn ${isActive ? 'active' : ''}`}
                            onClick={() => setIsActive(!isActive)}
                            title={isActive ? 'Active — click to disable' : 'Inactive — click to enable'}
                        >
                            {isActive ? <FaToggleOn /> : <FaToggleOff />}
                            <span>{isActive ? 'Active' : 'Inactive'}</span>
                        </button>
                        <button className="wfd-close-btn" onClick={() => onClose && onClose(false)}>
                            <FaTimes />
                        </button>
                    </div>
                </div>

                {/* Body */}
                <div className="wfd-body">
                    {/* Section 1: Basics */}
                    <div className="wfd-section">
                        <div className="wfd-section-title">
                            <span className="wfd-step-number">1</span>
                            Workflow Details
                        </div>
                        <div className="wfd-basics-grid">
                            <div className="wfd-config-field wfd-field-full">
                                <label>Workflow Name <span className="wfd-required">*</span></label>
                                <input
                                    type="text"
                                    value={name}
                                    onChange={e => setName(e.target.value)}
                                    className="wfd-input"
                                    placeholder="e.g. End-of-Day Access Revocation"
                                />
                            </div>
                            <div className="wfd-config-field wfd-field-full">
                                <label>Description</label>
                                <textarea
                                    value={description}
                                    onChange={e => setDescription(e.target.value)}
                                    className="wfd-textarea"
                                    rows={2}
                                    placeholder="Describe what this workflow does and when it should be used..."
                                />
                            </div>
                        </div>
                    </div>

                    {/* Section 2: Trigger */}
                    <div className="wfd-section">
                        <div className="wfd-section-title">
                            <span className="wfd-step-number">2</span>
                            When should this run?
                        </div>
                        <div className="wfd-trigger-grid">
                            {TRIGGER_TYPES.map(tt => (
                                <button
                                    key={tt.value}
                                    className={`wfd-trigger-card ${triggerType === tt.value ? 'selected' : ''}`}
                                    onClick={() => {
                                        setTriggerType(tt.value);
                                        setTriggerConfig({});
                                    }}
                                    style={{ '--trigger-color': tt.color }}
                                >
                                    <tt.icon className="wfd-trigger-icon" />
                                    <div className="wfd-trigger-label">{tt.label}</div>
                                    <div className="wfd-trigger-desc">{tt.description}</div>
                                </button>
                            ))}
                        </div>
                        {renderTriggerConfig()}
                    </div>

                    {/* Section 2.5: Conditions (optional guard) */}
                    <div className="wfd-section wfd-conditions-section">
                        <div className="wfd-section-title">
                            <FaFilter className="wfd-conditions-icon" />
                            Conditions (optional)
                            <small className="wfd-section-subtitle">Only run when these conditions are also met</small>
                        </div>
                        {renderConditionsBuilder()}
                    </div>

                    {/* Section 3: Steps */}
                    <div className="wfd-section">
                        <div className="wfd-section-title">
                            <span className="wfd-step-number">3</span>
                            Automation Steps
                            <span className="wfd-step-count">{steps.length} step{steps.length !== 1 ? 's' : ''}</span>
                        </div>

                        {steps.length === 0 && (
                            <div className="wfd-empty-steps">
                                <FaExclamationTriangle />
                                <span>No steps yet. Add automation steps to define what happens when this workflow runs.</span>
                            </div>
                        )}

                        <div className="wfd-steps-list">
                            {steps.map((step, index) => {
                                const IconComponent = ACTION_ICONS[step.actionType] || FaBolt;
                                const typeMeta = actionTypes.find(t => t.type === step.actionType);
                                const isExpanded = expandedStep === index;

                                return (
                                    <div key={step._id || index} className={`wfd-step-card ${!step.isActive ? 'disabled' : ''}`}>
                                        <div className="wfd-step-header" onClick={() => setExpandedStep(isExpanded ? null : index)}>
                                            <div className="wfd-step-left">
                                                <FaGripVertical className="wfd-step-grip" />
                                                <span className="wfd-step-order">{index + 1}</span>
                                                <IconComponent className="wfd-step-icon" />
                                                <div className="wfd-step-info">
                                                    <div className="wfd-step-name">{step.name}</div>
                                                    <div className="wfd-step-type">{typeMeta?.category || step.actionType}</div>
                                                </div>
                                            </div>
                                            <div className="wfd-step-right">
                                                {step.continueOnFailure && (
                                                    <span className="wfd-step-badge" title="Continues on failure">
                                                        Skip errors
                                                    </span>
                                                )}
                                                <button
                                                    className="wfd-step-btn"
                                                    onClick={e => { e.stopPropagation(); moveStep(index, -1); }}
                                                    disabled={index === 0}
                                                    title="Move up"
                                                >
                                                    <FaChevronUp />
                                                </button>
                                                <button
                                                    className="wfd-step-btn"
                                                    onClick={e => { e.stopPropagation(); moveStep(index, 1); }}
                                                    disabled={index === steps.length - 1}
                                                    title="Move down"
                                                >
                                                    <FaChevronDown />
                                                </button>
                                                <button
                                                    className="wfd-step-btn danger"
                                                    onClick={e => { e.stopPropagation(); removeStep(index); }}
                                                    title="Remove step"
                                                >
                                                    <FaTrash />
                                                </button>
                                            </div>
                                        </div>

                                        {isExpanded && (
                                            <div className="wfd-step-body">
                                                <div className="wfd-config-field">
                                                    <label>Step Name</label>
                                                    <input
                                                        type="text"
                                                        value={step.name}
                                                        onChange={e => updateStep(index, { name: e.target.value })}
                                                        className="wfd-input"
                                                    />
                                                </div>

                                                {typeMeta?.description && (
                                                    <div className="wfd-step-description">
                                                        <FaInfoCircle /> {typeMeta.description}
                                                    </div>
                                                )}

                                                {renderStepConfig(step, index)}

                                                <div className="wfd-step-options">
                                                    <label className="wfd-toggle-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={step.isActive}
                                                            onChange={e => updateStep(index, { isActive: e.target.checked })}
                                                        />
                                                        <span>Step enabled</span>
                                                    </label>
                                                    <label className="wfd-toggle-label">
                                                        <input
                                                            type="checkbox"
                                                            checked={step.continueOnFailure}
                                                            onChange={e => updateStep(index, { continueOnFailure: e.target.checked })}
                                                        />
                                                        <span>Continue on failure (don't abort workflow if this step fails)</span>
                                                    </label>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        {/* Add step button */}
                        <button className="wfd-add-step-btn" onClick={() => setShowAddStep(!showAddStep)}>
                            <FaPlus />
                            Add Automation Step
                        </button>

                        {/* Add step panel */}
                        {showAddStep && (
                            <div className="wfd-add-step-panel">
                                {loadingTypes ? (
                                    <div className="wfd-loading"><FaSpinner className="wfd-spinner" /> Loading available actions...</div>
                                ) : (
                                    Object.entries(groupedActions).map(([category, actions]) => (
                                        <div key={category} className="wfd-action-category">
                                            <div className="wfd-action-category-title">{category}</div>
                                            <div className="wfd-action-grid">
                                                {actions.map(action => {
                                                    const ActionIcon = ACTION_ICONS[action.type] || FaBolt;
                                                    return (
                                                        <button
                                                            key={action.type}
                                                            className="wfd-action-card"
                                                            onClick={() => addStep(action.type)}
                                                        >
                                                            <ActionIcon className="wfd-action-icon" />
                                                            <div className="wfd-action-label">{action.label}</div>
                                                            <div className="wfd-action-desc">{action.description}</div>
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="wfd-footer">
                    <button className="wfd-btn wfd-btn-cancel" onClick={() => onClose && onClose(false)} disabled={saving}>
                        Cancel
                    </button>
                    <button className="wfd-btn wfd-btn-save" onClick={handleSave} disabled={saving}>
                        {saving ? (
                            <>
                                <FaSpinner className="wfd-spinner" />
                                Saving...
                            </>
                        ) : (
                            <>
                                <FaSave />
                                {existingWorkflow ? 'Update Workflow' : 'Create Workflow'}
                            </>
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WorkflowDesigner;
